import { Link } from 'react-router-dom';
import { ArrowLeft, Heart, Globe, Target, ArrowRight, Mail } from 'lucide-react';

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-gray-500 hover:text-gray-700">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-2xl font-bold text-gray-900">About Us</h1>
          </div>
        </div>
      </header>

      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Bridging the Gap in African Healthcare</h2>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            OmniClinicalBridge provides evidence-based clinical decision support designed specifically for the unique challenges of healthcare delivery in Africa.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <section className="mb-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-sm text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Target className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Our Mission</h3>
              <p className="text-gray-600">To democratize access to high-quality clinical decision support, empowering healthcare workers across Africa with the knowledge they need to save lives.</p>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-sm text-center">
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Globe className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Our Vision</h3>
              <p className="text-gray-600">A future where every healthcare professional in Africa has instant access to evidence-based protocols tailored to their resource setting.</p>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-sm text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Our Values</h3>
              <p className="text-gray-600">Evidence-based practice, resource-appropriate solutions, accessibility, and unwavering commitment to improving patient outcomes.</p>
            </div>
          </div>
        </section>

        <section className="mb-16">
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="md:flex">
              <div className="md:w-1/3 bg-gradient-to-br from-blue-600 to-blue-800 p-8 flex items-center justify-center">
                <div className="text-center text-white">
                  <div className="w-48 h-48 bg-white rounded-full mx-auto mb-4 flex items-center justify-center overflow-hidden shadow-xl">
                    <img src="/images/founder.png" alt="Dr. Omokayode Abiola Aderolu" className="w-full h-full object-contain" />
                  </div>
                  <h3 className="text-2xl font-bold">Dr. Omokayode Abiola Aderolu</h3>
                  <p className="text-blue-200">Founder and CEO</p>
                </div>
              </div>
              <div className="md:w-2/3 p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">About the Founder</h2>
                <p className="text-gray-700 mb-4">Dr. Omokayode Abiola Aderolu is a physician with over 20 years of clinical experience across Nigeria and the United Kingdom. His firsthand experience with the challenges of healthcare delivery in resource-limited settings inspired him to create solutions that bridge the gap between clinical knowledge and practical implementation.</p>
                <p className="text-gray-700 mb-6">Combining his medical expertise with technical prowess, Dr. Aderolu holds an MD, an MSc in Biomedical Engineering, and over 10 cloud certifications across AWS, Azure, GCP, and Oracle. He has solo-built 19 healthcare platforms comprising over 500,000 lines of production code, targeting serving users across 54 African countries.</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">20+</div>
                    <div className="text-sm text-gray-500">Years Clinical Experience</div>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">10+</div>
                    <div className="text-sm text-gray-500">Cloud Certifications</div>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">500K+</div>
                    <div className="text-sm text-gray-500">Lines of Code</div>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">54</div>
                    <div className="text-sm text-gray-500">Target African Countries</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-4">
                  <a href="mailto:ceo@omnialera.com" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors">
                    <Mail className="w-4 h-4" /> ceo@omnialera.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mb-16">
          <div className="bg-gradient-to-r from-pink-500 to-rose-500 rounded-2xl p-8 text-white text-center">
            <h2 className="text-2xl font-bold mb-4">Part of the OmniAlera Healthcare Empire</h2>
            <p className="text-pink-100 mb-6 max-w-2xl mx-auto">OmniClinicalBridge is Division 3 of OmniAlera Technologies Holdings Inc., a Delaware C-Corporation building the future of African healthcare through AI-powered platforms.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <div className="bg-white/20 px-4 py-2 rounded-lg">
                <div className="font-bold">10</div>
                <div className="text-sm text-pink-100">Divisions</div>
              </div>
              <div className="bg-white/20 px-4 py-2 rounded-lg">
                <div className="font-bold">Target 10,000+</div>
                <div className="text-sm text-pink-100">Users</div>
              </div>
              <div className="bg-white/20 px-4 py-2 rounded-lg">
                <div className="font-bold">Target 54</div>
                <div className="text-sm text-pink-100">African Countries</div>
              </div>
            </div>
            <a href="https://omnialera.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-6 px-6 py-3 bg-white text-pink-600 font-semibold rounded-full hover:bg-pink-50 transition-colors">
              View Full Portfolio <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">Our Impact</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-sm text-center">
              <div className="text-3xl font-bold text-blue-600 mb-2">100</div>
              <div className="text-gray-600">Clinical Conditions</div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm text-center">
              <div className="text-3xl font-bold text-green-600 mb-2">50</div>
              <div className="text-gray-600">Essential Medicines</div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm text-center">
              <div className="text-3xl font-bold text-purple-600 mb-2">5</div>
              <div className="text-gray-600">Languages</div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm text-center">
              <div className="text-3xl font-bold text-red-600 mb-2">3</div>
              <div className="text-gray-600">Resource Levels</div>
            </div>
          </div>
        </section>
      </div>

      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="font-semibold mb-2">OmniAlera Technologies Holdings Inc.</p>
          <p className="text-gray-400 text-sm">Delaware C-Corporation</p>
          <p className="text-gray-400 text-sm mt-2">Division 3 - OmniClinicalBridge V2.0</p>
        </div>
      </footer>
    </div>
  );
}
